import { useState, useCallback } from "react";
import { type Folder, fetchFolders, createFolder, renameFolder, deleteFolder } from "../services/folderService";
import { toast } from "react-hot-toast";

export function useFolders(translatorServiceUrl: string) {
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadFolders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchFolders(translatorServiceUrl);
      setFolders(data);
    } catch (err: any) {
      console.error("Error loading folders:", err);
      setError(err.message || "Failed to load folders");
    } finally {
      setLoading(false);
    }
  }, [translatorServiceUrl]);

  const handleCreateFolder = useCallback(async (name: string, isEn = false) => {
    if (!name.trim()) return null;
    try {
      const folderId = await createFolder(translatorServiceUrl, name.trim());
      await loadFolders();
      toast.success(isEn ? "Folder created successfully" : "Carpeta creada exitosamente");
      return folderId;
    } catch (err) {
      console.error("Error creating folder:", err);
      toast.error(isEn ? "Failed to create folder" : "Error al crear carpeta");
      return null;
    }
  }, [translatorServiceUrl, loadFolders]);

  const handleRenameFolder = useCallback(async (folderId: string | number, name: string, isEn = false) => {
    if (!name.trim()) return false;
    try {
      await renameFolder(translatorServiceUrl, folderId, name.trim());
      await loadFolders();
      toast.success(isEn ? "Folder renamed successfully" : "Carpeta renombrada exitosamente");
      return true;
    } catch (err) {
      console.error("Error renaming folder:", err);
      toast.error(isEn ? "Failed to rename folder" : "Error al renombrar carpeta");
      return false;
    }
  }, [translatorServiceUrl, loadFolders]);

  const handleDeleteFolder = useCallback(async (folderId: string | number, isEn = false) => {
    try {
      await deleteFolder(translatorServiceUrl, folderId);
      await loadFolders();
      toast.success(isEn ? "Folder deleted successfully" : "Carpeta eliminada exitosamente");
      return true;
    } catch (err) {
      console.error("Error deleting folder:", err);
      toast.error(isEn ? "Failed to delete folder" : "Error al eliminar carpeta");
      return false;
    }
  }, [translatorServiceUrl, loadFolders]);

  return {
    folders,
    loading,
    error,
    loadFolders,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
  };
}
